import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

const PanelContainer = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 1.5rem;
`;

const PanelTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const FormGroup = styled.div`
  flex: 1;
  min-width: 200px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.6rem 0.8rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.6rem 0.8rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.6rem 0.8rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'success' | 'danger' }>`
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' && `
    background: #4299e1;
    color: white;
    &:hover { background: #3182ce; }
  `}
  
  ${props => props.variant === 'success' && `
    background: #48bb78;
    color: white;
    &:hover { background: #38a169; }
  `}
  
  ${props => props.variant === 'danger' && `
    background: #fc8181;
    color: white;
    &:hover { background: #f56565; }
  `}
  
  ${props => !props.variant && `
    background: #e2e8f0;
    color: #4a5568;
    &:hover { background: #cbd5e0; }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ type: 'success' | 'error' | 'info' }>`
  padding: 0.75rem 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  
  ${props => props.type === 'success' && `
    background: #c6f6d5;
    color: #22543d;
    border: 1px solid #9ae6b4;
  `}
  
  ${props => props.type === 'error' && `
    background: #fed7d7;
    color: #742a2a;
    border: 1px solid #feb2b2;
  `}
  
  ${props => props.type === 'info' && `
    background: #bee3f8;
    color: #2a4365;
    border: 1px solid #90cdf4;
  `}
`;

const RequestList = styled.div`
  margin-top: 1.5rem;
`;

const RequestItem = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
`;

const RequestHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const RequestStatus = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => props.status === 'pending' && `
    background: #fef3c7;
    color: #92400e;
  `}
  
  ${props => props.status === 'uploaded' && `
    background: #dbeafe;
    color: #1e40af;
  `}
  
  ${props => props.status === 'verified' && `
    background: #d1fae5;
    color: #065f46;
  `}
  
  ${props => props.status === 'rejected' && `
    background: #fee2e2;
    color: #991b1b;
  `}
  
  ${props => props.status === 'expired' && `
    background: #e5e7eb;
    color: #6b7280;
  `}
`;

const RequestDetails = styled.div`
  font-size: 0.85rem;
  color: #4a5568;
  
  p {
    margin: 0.25rem 0;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

// Modal styles for document preview
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: #1a202c;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #718096;
  padding: 0.25rem;
  line-height: 1;
  
  &:hover {
    color: #1a202c;
  }
`;

const OpenInNewTabLink = styled.a`
  color: #4299e1;
  text-decoration: none;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ModalBody = styled.div`
  padding: 1rem;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  max-height: calc(90vh - 80px);
  background: #f1f5f9;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
`;

const PreviewPdf = styled.iframe`
  width: 800px;
  height: 70vh;
  border: none;
`;

interface DocumentRequest {
  _id: string;
  applicationId: string;
  recipientEmail: string;
  recipientName: string;
  recipientType: string;
  documentType: string;
  customDocumentName: string | null;
  message: string;
  status: string;
  uploadedFile: string | null;
  uploadedAt: string | null;
  verifiedAt: string | null;
  createdAt: string;
}

interface Props {
  applicationId: string;
  applicantEmail: string;
  applicantName: string;
  partners?: Array<{
    email?: string;
    name?: string;
    personalMobile?: string;
  }>;
}

const DOCUMENT_TYPES = [
  { value: 'affidavit', label: 'Affidavit' },
  { value: 'pan_card', label: 'PAN Card' },
  { value: 'address_proof', label: 'Address Proof' },
  { value: 'id_proof', label: 'ID Proof' },
  { value: 'qualification_proof', label: 'Qualification Proof' },
  { value: 'land_document', label: 'Land Document' },
  { value: 'custom', label: 'Custom Document' }
];

const DocumentRequestPanel: React.FC<Props> = ({
  applicationId,
  applicantEmail,
  applicantName,
  partners = []
}) => {
  const [requests, setRequests] = useState<DocumentRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  
  // Form state
  const [recipientType, setRecipientType] = useState<'applicant' | 'partner'>('applicant');
  const [selectedPartnerIndex, setSelectedPartnerIndex] = useState<number>(0);
  const [documentType, setDocumentType] = useState('affidavit');
  const [customDocumentName, setCustomDocumentName] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  
  // Modal state for document preview
  const [previewModal, setPreviewModal] = useState<{ isOpen: boolean; fileUrl: string; fileName: string }>({ 
    isOpen: false, 
    fileUrl: '', 
    fileName: '' 
  });

  // Helper to get the correct file URL pointing to backend server
  const getFileUrl = (filePath: string) => {
    // API_BASE already contains the backend URL (e.g., http://localhost:5000)
    let path = filePath;
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    return `${API_BASE}${path}`;
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE}/api/admin/document-requests/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setRequests(response.data.requests);
      }
    } catch (error) {
      console.error('Error fetching document requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [applicationId]);

  const handleSendRequest = async () => {
    if (documentType === 'custom' && !customDocumentName.trim()) {
      setMessage({ type: 'error', text: 'Please enter a custom document name.' });
      return;
    }

    const recipientEmail = recipientType === 'applicant' 
      ? applicantEmail 
      : partners[selectedPartnerIndex]?.email;
    
    const recipientName = recipientType === 'applicant'
      ? applicantName
      : partners[selectedPartnerIndex]?.name;

    if (!recipientEmail) {
      setMessage({ type: 'error', text: 'No email address available for the selected recipient.' });
      return;
    }

    try {
      setSending(true);
      setMessage(null);
      
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_BASE}/api/admin/document-request/send`,
        {
          applicationId,
          recipientEmail,
          recipientName,
          recipientType,
          partnerIndex: recipientType === 'partner' ? selectedPartnerIndex : undefined,
          documentType,
          customDocumentName: documentType === 'custom' ? customDocumentName : undefined,
          message: customMessage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setMessage({ type: 'success', text: `Document request sent to ${recipientEmail}` });
        setCustomMessage('');
        setCustomDocumentName('');
        fetchRequests();
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to send request' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to send request' });
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async (requestId: string, action: 'verify' | 'reject') => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_BASE}/api/admin/document-request/${requestId}/verify`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setMessage({ type: 'success', text: `Document ${action}ed successfully` });
        fetchRequests();
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Action failed' });
    }
  };

  const viewUploadedFile = (filePath: string) => {
    const fileUrl = getFileUrl(filePath);
    const fileName = filePath.split('/').pop() || 'document';
    setPreviewModal({ isOpen: true, fileUrl, fileName });
  };

  const closePreviewModal = () => {
    setPreviewModal({ isOpen: false, fileUrl: '', fileName: '' });
  };

  const isPdfFile = (url: string) => {
    return url.toLowerCase().endsWith('.pdf');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <>
    {/* Document Preview Modal */}
    {previewModal.isOpen && (
      <ModalOverlay onClick={closePreviewModal}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>📄 {previewModal.fileName}</ModalTitle>
            <ModalActions>
              <OpenInNewTabLink href={previewModal.fileUrl} target="_blank" rel="noopener noreferrer">
                🔗 Open in new tab
              </OpenInNewTabLink>
              <ModalCloseButton onClick={closePreviewModal}>×</ModalCloseButton>
            </ModalActions>
          </ModalHeader>
          <ModalBody>
            {isPdfFile(previewModal.fileUrl) ? (
              <PreviewPdf src={previewModal.fileUrl} title="Document Preview" />
            ) : (
              <PreviewImage src={previewModal.fileUrl} alt="Document Preview" />
            )}
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    )}
    
    <PanelContainer>
      <PanelTitle>
        📧 Request Documents via Email
      </PanelTitle>

      {message && <Message type={message.type}>{message.text}</Message>}

      <FormRow>
        <FormGroup>
          <Label>Recipient</Label>
          <Select 
            value={recipientType === 'applicant' ? 'applicant' : `partner_${selectedPartnerIndex}`}
            onChange={(e) => {
              if (e.target.value === 'applicant') {
                setRecipientType('applicant');
              } else {
                setRecipientType('partner');
                setSelectedPartnerIndex(parseInt(e.target.value.split('_')[1]));
              }
            }}
          >
            <option value="applicant">
              Primary Applicant - {applicantName || applicantEmail}
            </option>
            {partners.map((partner, idx) => (
              <option key={idx} value={`partner_${idx}`}>
                Partner {idx + 1} - {partner.name || partner.email || `No email`}
              </option>
            ))}
          </Select>
        </FormGroup>
        
        <FormGroup>
          <Label>Document Type</Label>
          <Select value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
            {DOCUMENT_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </Select>
        </FormGroup>
      </FormRow>

      {documentType === 'custom' && (
        <FormRow>
          <FormGroup style={{ flex: 1 }}>
            <Label>Custom Document Name</Label>
            <Input
              type="text"
              value={customDocumentName}
              onChange={(e) => setCustomDocumentName(e.target.value)}
              placeholder="Enter document name"
            />
          </FormGroup>
        </FormRow>
      )}

      <FormRow>
        <FormGroup style={{ flex: 1 }}>
          <Label>Message to Recipient (Optional)</Label>
          <TextArea
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Add any additional instructions or context..."
          />
        </FormGroup>
      </FormRow>

      <Button variant="primary" onClick={handleSendRequest} disabled={sending}>
        {sending ? 'Sending...' : '📤 Send Document Request'}
      </Button>

      <RequestList>
        <h4 style={{ fontSize: '1rem', margin: '0 0 1rem 0', color: '#2d3748' }}>
          Previous Requests ({requests.length})
        </h4>
        
        {loading ? (
          <p style={{ color: '#718096' }}>Loading requests...</p>
        ) : requests.length === 0 ? (
          <p style={{ color: '#718096', fontStyle: 'italic' }}>No document requests sent yet.</p>
        ) : (
          requests.map(request => (
            <RequestItem key={request._id}>
              <RequestHeader>
                <strong>
                  {request.documentType === 'custom' 
                    ? request.customDocumentName 
                    : DOCUMENT_TYPES.find(t => t.value === request.documentType)?.label || request.documentType}
                </strong>
                <RequestStatus status={request.status}>{request.status}</RequestStatus>
              </RequestHeader>
              <RequestDetails>
                <p><strong>To:</strong> {request.recipientName || request.recipientEmail}</p>
                <p><strong>Sent:</strong> {formatDate(request.createdAt)}</p>
                {request.uploadedAt && (
                  <p><strong>Uploaded:</strong> {formatDate(request.uploadedAt)}</p>
                )}
              </RequestDetails>
              
              {request.status === 'uploaded' && (
                <ActionButtons>
                  <Button 
                    variant="success" 
                    onClick={() => handleVerify(request._id, 'verify')}
                  >
                    ✓ Verify
                  </Button>
                  <Button 
                    variant="danger" 
                    onClick={() => handleVerify(request._id, 'reject')}
                  >
                    ✕ Reject
                  </Button>
                  {request.uploadedFile && (
                    <Button onClick={() => viewUploadedFile(request.uploadedFile!)}>
                      👁 View Document
                    </Button>
                  )}
                </ActionButtons>
              )}
              
              {request.status === 'verified' && request.uploadedFile && (
                <ActionButtons>
                  <Button onClick={() => viewUploadedFile(request.uploadedFile!)}>
                    👁 View Document
                  </Button>
                </ActionButtons>
              )}
            </RequestItem>
          ))
        )}
      </RequestList>
    </PanelContainer>
    </>
  );
};

export default DocumentRequestPanel;
